/**
 * Geo-blocking utility for EU users
 * Implements client-side geo-detection for MVP launch phase
 */

// EU countries list (ISO 3166-1 alpha-2 codes)
const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
];

// Additional EEA countries that should follow GDPR
const EEA_COUNTRIES = ['IS', 'LI', 'NO'];

// UK (post-Brexit but with similar data protection laws)
const UK_COUNTRIES = ['GB'];

// All blocked countries for GDPR compliance
const BLOCKED_COUNTRIES = [...EU_COUNTRIES, ...EEA_COUNTRIES, ...UK_COUNTRIES];

/**
 * Detect user's country using multiple methods
 * @returns {Promise<string|null>} Country code or null if detection fails
 */
export const detectUserCountry = async () => {
  try {
    // Method 1: Try timezone-based detection first (fastest)
    const timezoneCountry = getCountryFromTimezone();
    if (timezoneCountry && BLOCKED_COUNTRIES.includes(timezoneCountry)) {
      return timezoneCountry;
    }

    // Method 2: Use IP geolocation API (fallback)
    const ipCountry = await getCountryFromIP();
    if (ipCountry) {
      return ipCountry;
    }

    // Method 3: Browser language as last resort
    const languageCountry = getCountryFromLanguage();
    return languageCountry;

  } catch (error) {
    console.warn('Geo-detection failed:', error);
    return null;
  }
};

/**
 * Check if user is from EU/EEA/UK (blocked region)
 * @returns {Promise<boolean>} True if user should be blocked
 */
export const isBlockedRegion = async () => {
  try {
    const country = await detectUserCountry();
    
    // If we can't detect, allow access (fail open for MVP)
    if (!country) {
      return false;
    }

    const isBlocked = BLOCKED_COUNTRIES.includes(country.toUpperCase());
    
    // Log geo-blocking decision for analytics
    if (isBlocked) {
      console.log(`🚫 Geo-blocking user from ${country} (EU/EEA/UK)`);
      // Track in analytics (non-PII)
      if (window.gtag) {
        window.gtag('event', 'geo_block', {
          'country': country,
          'reason': 'gdpr_compliance'
        });
      }
    }

    return isBlocked;
  } catch (error) {
    console.warn('Geo-blocking check failed:', error);
    // Fail open - allow access if detection fails
    return false;
  }
};

/**
 * Get country from user's timezone (fast, privacy-friendly)
 * Limited accuracy but good for major EU countries
 * @returns {string|null} Country code or null
 */
const getCountryFromTimezone = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Map major EU timezones to countries
    const timezoneMap = {
      'Europe/London': 'GB',
      'Europe/Dublin': 'IE',
      'Europe/Paris': 'FR',
      'Europe/Berlin': 'DE',
      'Europe/Rome': 'IT',
      'Europe/Madrid': 'ES',
      'Europe/Amsterdam': 'NL',
      'Europe/Brussels': 'BE',
      'Europe/Vienna': 'AT',
      'Europe/Stockholm': 'SE',
      'Europe/Copenhagen': 'DK',
      'Europe/Helsinki': 'FI',
      'Europe/Oslo': 'NO',
      'Europe/Zurich': 'CH',
      'Europe/Prague': 'CZ',
      'Europe/Warsaw': 'PL',
      'Europe/Budapest': 'HU',
      'Europe/Athens': 'GR',
      'Europe/Lisbon': 'PT',
      'Europe/Luxembourg': 'LU',
      'Europe/Tallinn': 'EE',
      'Europe/Riga': 'LV',
      'Europe/Vilnius': 'LT'
    };

    return timezoneMap[timezone] || null;
  } catch (error) {
    console.warn('Timezone detection failed:', error);
    return null;
  }
};

/**
 * Get country from IP address using free geolocation API
 * @returns {Promise<string|null>} Country code or null
 */
const getCountryFromIP = async () => {
  try {
    // Use ipapi.co free tier (1000 requests/day)
    const response = await fetch('https://ipapi.co/country/', {
      method: 'GET',
      headers: {
        'Accept': 'text/plain'
      }
    });

    if (response.ok) {
      const country = await response.text();
      return country.trim().toUpperCase();
    }

    return null;
  } catch (error) {
    console.warn('IP geolocation failed:', error);
    return null;
  }
};

/**
 * Get likely country from browser language (least accurate)
 * @returns {string|null} Country code or null
 */
const getCountryFromLanguage = () => {
  try {
    const language = navigator.language || navigator.userLanguage;
    
    // Extract country from language tag (e.g., "en-GB" -> "GB")
    if (language && language.includes('-')) {
      const countryCode = language.split('-')[1].toUpperCase();
      return BLOCKED_COUNTRIES.includes(countryCode) ? countryCode : null;
    }

    return null;
  } catch (error) {
    console.warn('Language detection failed:', error);
    return null;
  }
};

/**
 * Store geo-blocking result in localStorage to avoid repeated checks
 * @param {boolean} isBlocked Whether user is blocked
 */
export const storeGeoBlockingResult = (isBlocked) => {
  try {
    const result = {
      isBlocked,
      timestamp: Date.now(),
      // Expire after 24 hours
      expiresAt: Date.now() + (24 * 60 * 60 * 1000)
    };
    localStorage.setItem('geo_blocking_result', JSON.stringify(result));
  } catch (error) {
    console.warn('Failed to store geo-blocking result:', error);
  }
};

/**
 * Get cached geo-blocking result if available and not expired
 * @returns {boolean|null} Cached result or null if not available/expired
 */
export const getCachedGeoBlockingResult = () => {
  try {
    const cached = localStorage.getItem('geo_blocking_result');
    if (!cached) return null;

    const result = JSON.parse(cached);
    
    // Check if expired
    if (Date.now() > result.expiresAt) {
      localStorage.removeItem('geo_blocking_result');
      return null;
    }

    return result.isBlocked;
  } catch (error) {
    console.warn('Failed to get cached geo-blocking result:', error);
    localStorage.removeItem('geo_blocking_result');
    return null;
  }
};

/**
 * Main geo-blocking check with caching
 * @returns {Promise<boolean>} True if user should be blocked
 */
export const checkGeoBlocking = async () => {
  // Check cache first
  const cached = getCachedGeoBlockingResult();
  if (cached !== null) {
    return cached;
  }

  // Perform fresh check
  const isBlocked = await isBlockedRegion();
  
  // Cache the result
  storeGeoBlockingResult(isBlocked);
  
  return isBlocked;
};